/**
 * Error Code Types and Mapping
 *
 * Error codes for API responses that can be localized on the frontend
 * This file mirrors the backend error codes for frontend use
 */

import type { TFunction } from 'i18next';

/**
 * General API Error Codes
 *
 * These codes are returned by the backend API and mapped to localized messages on the frontend
 */
export enum ErrorCode {
  // Validation errors (1xxx)
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  REQUIRED_FIELD_MISSING = 'REQUIRED_FIELD_MISSING',
  INVALID_FORMAT = 'INVALID_FORMAT',
  INVALID_LENGTH = 'INVALID_LENGTH',
  INVALID_RANGE = 'INVALID_RANGE',
  INVALID_TYPE = 'INVALID_TYPE',
  INVALID_DATE = 'INVALID_DATE',
  INVALID_EMAIL = 'INVALID_EMAIL',
  INVALID_PHONE = 'INVALID_PHONE',
  DUPLICATE_VALUE = 'DUPLICATE_VALUE',
  MASTER_DATA_VALIDATION_FAILED = 'MASTER_DATA_VALIDATION_FAILED',
  TARGET_RECORD_STALE = 'TARGET_RECORD_STALE',
  TARGET_RECORD_NOT_FOUND = 'TARGET_RECORD_NOT_FOUND',
  TARGET_RECORD_INACTIVE = 'TARGET_RECORD_INACTIVE',
  ILLEGAL_DRAFT_STATE = 'ILLEGAL_DRAFT_STATE',
  MASTER_DATA_DRAFT_PERMISSION_DENIED = 'MASTER_DATA_DRAFT_PERMISSION_DENIED',

  // Authentication errors (2xxx)
  UNAUTHORIZED = 'UNAUTHORIZED',
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',
  INVALID_REFRESH_TOKEN = 'INVALID_REFRESH_TOKEN',
  TOKEN_EXPIRED = 'TOKEN_EXPIRED',
  TOKEN_INVALID = 'TOKEN_INVALID',
  PERMISSION_DENIED = 'PERMISSION_DENIED',
  SESSION_EXPIRED = 'SESSION_EXPIRED',

  // Resource errors (3xxx)
  NOT_FOUND = 'NOT_FOUND',
  RESOURCE_NOT_FOUND = 'RESOURCE_NOT_FOUND',
  RECORD_NOT_FOUND = 'RECORD_NOT_FOUND',
  ENTITY_NOT_FOUND = 'ENTITY_NOT_FOUND',

  // Business logic errors (4xxx)
  BUSINESS_RULE_VIOLATION = 'BUSINESS_RULE_VIOLATION',
  INVALID_STATUS_TRANSITION = 'INVALID_STATUS_TRANSITION',
  OPERATION_NOT_ALLOWED = 'OPERATION_NOT_ALLOWED',
  INSUFFICIENT_STOCK = 'INSUFFICIENT_STOCK',
  INSUFFICIENT_CREDIT = 'INSUFFICIENT_CREDIT',
  DUPLICATE_ORDER = 'DUPLICATE_ORDER',
  PRICE_MISMATCH = 'PRICE_MISMATCH',
  QUANTITY_EXCEEDED = 'QUANTITY_EXCEEDED',
  CONFLICT = 'CONFLICT',

  // Inventory specific errors (41xx)
  INVENTORY_NEGATIVE_STOCK = 'INVENTORY_NEGATIVE_STOCK',
  INVENTORY_RESERVATION_FAILED = 'INVENTORY_RESERVATION_FAILED',
  INVENTORY_RELEASE_FAILED = 'INVENTORY_RELEASE_FAILED',
  INVENTORY_COUNT_MISMATCH = 'INVENTORY_COUNT_MISMATCH',
  WAREHOUSE_NOT_FOUND = 'WAREHOUSE_NOT_FOUND',
  BATCH_NOT_FOUND = 'BATCH_NOT_FOUND',

  // Order specific errors (42xx)
  ORDER_ALREADY_CONFIRMED = 'ORDER_ALREADY_CONFIRMED',
  ORDER_ALREADY_CANCELLED = 'ORDER_ALREADY_CANCELLED',
  ORDER_ALREADY_COMPLETED = 'ORDER_ALREADY_COMPLETED',
  ORDER_ITEM_NOT_FOUND = 'ORDER_ITEM_NOT_FOUND',
  SHIPMENT_FAILED = 'SHIPMENT_FAILED',
  RETURN_NOT_ALLOWED = 'RETURN_NOT_ALLOWED',

  // Sales order / shipment / return lifecycle errors (surfaced by the ERP UI)
  ORDER_NOT_FOUND = 'ORDER_NOT_FOUND',
  INVALID_ORDER_STATUS = 'INVALID_ORDER_STATUS',
  INACTIVE_CUSTOMER = 'INACTIVE_CUSTOMER',
  SHIPMENT_NOT_FOUND = 'SHIPMENT_NOT_FOUND',
  INVALID_SHIPMENT_QUANTITY = 'INVALID_SHIPMENT_QUANTITY',
  SHIPMENT_ALREADY_CONFIRMED = 'SHIPMENT_ALREADY_CONFIRMED',
  RETURN_NOT_FOUND = 'RETURN_NOT_FOUND',
  INVALID_RETURN_QUANTITY = 'INVALID_RETURN_QUANTITY',

  // Quotation / pricing errors
  QUOTATION_NOT_FOUND = 'QUOTATION_NOT_FOUND',
  QUOTATION_ALREADY_CONVERTED = 'QUOTATION_ALREADY_CONVERTED',
  QUOTATION_EXPIRED = 'QUOTATION_EXPIRED',
  PRICE_LIST_NOT_FOUND = 'PRICE_LIST_NOT_FOUND',
  NO_ACTIVE_PRICE_LIST = 'NO_ACTIVE_PRICE_LIST',
  PRICE_NOT_FOUND = 'PRICE_NOT_FOUND',

  // Production specific errors (43xx)
  BOM_NOT_FOUND = 'BOM_NOT_FOUND',
  WORK_ORDER_NOT_FOUND = 'WORK_ORDER_NOT_FOUND',
  PRODUCTION_PLAN_CONFLICT = 'PRODUCTION_PLAN_CONFLICT',
  MATERIAL_SHORTAGE = 'MATERIAL_SHORTAGE',

  // Finance specific errors (44xx)
  PAYMENT_FAILED = 'PAYMENT_FAILED',
  INVOICE_ALREADY_PAID = 'INVOICE_ALREADY_PAID',
  VOUCHER_NUMBER_CONFLICT = 'VOUCHER_NUMBER_CONFLICT',
  FISCAL_PERIOD_CLOSED = 'FISCAL_PERIOD_CLOSED',

  // Database errors (5xxx)
  DATABASE_ERROR = 'DATABASE_ERROR',
  CONNECTION_ERROR = 'CONNECTION_ERROR',
  QUERY_ERROR = 'QUERY_ERROR',
  TRANSACTION_ERROR = 'TRANSACTION_ERROR',
  CONSTRAINT_VIOLATION = 'CONSTRAINT_VIOLATION',
  UNIQUE_VIOLATION = 'UNIQUE_VIOLATION',
  FOREIGN_KEY_VIOLATION = 'FOREIGN_KEY_VIOLATION',

  // External service errors (6xxx)
  EXTERNAL_SERVICE_ERROR = 'EXTERNAL_SERVICE_ERROR',
  LLM_SERVICE_ERROR = 'LLM_SERVICE_ERROR',
  TIMEOUT_ERROR = 'TIMEOUT_ERROR',
  RATE_LIMIT_EXCEEDED = 'RATE_LIMIT_EXCEEDED',

  // System errors (9xxx)
  INTERNAL_ERROR = 'INTERNAL_ERROR',
  CONFIGURATION_ERROR = 'CONFIGURATION_ERROR',
  FEATURE_NOT_IMPLEMENTED = 'FEATURE_NOT_IMPLEMENTED',
  MAINTENANCE_MODE = 'MAINTENANCE_MODE',
  SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',
}

/**
 * Error code to translation key mapping
 */
const ERROR_CODE_KEY_MAP: Record<ErrorCode, string> = {
  // Validation errors
  [ErrorCode.VALIDATION_ERROR]: 'errors:validation.default',
  [ErrorCode.REQUIRED_FIELD_MISSING]: 'errors:validation.required',
  [ErrorCode.INVALID_FORMAT]: 'errors:validation.format',
  [ErrorCode.INVALID_LENGTH]: 'errors:validation.length',
  [ErrorCode.INVALID_RANGE]: 'errors:validation.range',
  [ErrorCode.INVALID_TYPE]: 'errors:validation.type',
  [ErrorCode.INVALID_DATE]: 'errors:validation.date',
  [ErrorCode.INVALID_EMAIL]: 'errors:validation.email',
  [ErrorCode.INVALID_PHONE]: 'errors:validation.phone',
  [ErrorCode.DUPLICATE_VALUE]: 'errors:validation.duplicate',
  [ErrorCode.MASTER_DATA_VALIDATION_FAILED]: 'errors:masterDataDraft.validationFailed',
  [ErrorCode.TARGET_RECORD_STALE]: 'errors:masterDataDraft.targetRecordStale',
  [ErrorCode.TARGET_RECORD_NOT_FOUND]: 'errors:masterDataDraft.targetRecordNotFound',
  [ErrorCode.TARGET_RECORD_INACTIVE]: 'errors:masterDataDraft.targetRecordInactive',
  [ErrorCode.ILLEGAL_DRAFT_STATE]: 'errors:masterDataDraft.illegalDraftState',
  [ErrorCode.MASTER_DATA_DRAFT_PERMISSION_DENIED]: 'errors:masterDataDraft.permissionDenied',

  // Authentication errors
  [ErrorCode.UNAUTHORIZED]: 'errors:auth.unauthorized',
  [ErrorCode.INVALID_CREDENTIALS]: 'errors:auth.invalidCredentials',
  [ErrorCode.INVALID_REFRESH_TOKEN]: 'errors:auth.invalidRefreshToken',
  [ErrorCode.TOKEN_EXPIRED]: 'errors:auth.tokenExpired',
  [ErrorCode.TOKEN_INVALID]: 'errors:auth.tokenInvalid',
  [ErrorCode.PERMISSION_DENIED]: 'errors:auth.permissionDenied',
  [ErrorCode.SESSION_EXPIRED]: 'errors:auth.sessionExpired',

  // Resource errors
  [ErrorCode.NOT_FOUND]: 'errors:notFound.default',
  [ErrorCode.RESOURCE_NOT_FOUND]: 'errors:notFound.resource',
  [ErrorCode.RECORD_NOT_FOUND]: 'errors:notFound.record',
  [ErrorCode.ENTITY_NOT_FOUND]: 'errors:notFound.entity',

  // Business logic errors
  [ErrorCode.BUSINESS_RULE_VIOLATION]: 'errors:business.ruleViolation',
  [ErrorCode.INVALID_STATUS_TRANSITION]: 'errors:business.invalidStatus',
  [ErrorCode.OPERATION_NOT_ALLOWED]: 'errors:business.operationNotAllowed',
  [ErrorCode.INSUFFICIENT_STOCK]: 'errors:business.insufficientStock',
  [ErrorCode.INSUFFICIENT_CREDIT]: 'errors:business.insufficientCredit',
  [ErrorCode.DUPLICATE_ORDER]: 'errors:business.duplicateOrder',
  [ErrorCode.PRICE_MISMATCH]: 'errors:business.priceMismatch',
  [ErrorCode.QUANTITY_EXCEEDED]: 'errors:business.quantityExceeded',
  [ErrorCode.CONFLICT]: 'errors:business.conflict',

  // Inventory specific errors
  [ErrorCode.INVENTORY_NEGATIVE_STOCK]: 'errors:inventory.negativeStock',
  [ErrorCode.INVENTORY_RESERVATION_FAILED]: 'errors:inventory.reservationFailed',
  [ErrorCode.INVENTORY_RELEASE_FAILED]: 'errors:inventory.releaseFailed',
  [ErrorCode.INVENTORY_COUNT_MISMATCH]: 'errors:inventory.countMismatch',
  [ErrorCode.WAREHOUSE_NOT_FOUND]: 'errors:inventory.warehouseNotFound',
  [ErrorCode.BATCH_NOT_FOUND]: 'errors:inventory.batchNotFound',

  // Order specific errors
  [ErrorCode.ORDER_ALREADY_CONFIRMED]: 'errors:order.alreadyConfirmed',
  [ErrorCode.ORDER_ALREADY_CANCELLED]: 'errors:order.alreadyCancelled',
  [ErrorCode.ORDER_ALREADY_COMPLETED]: 'errors:order.alreadyCompleted',
  [ErrorCode.ORDER_ITEM_NOT_FOUND]: 'errors:order.itemNotFound',
  [ErrorCode.SHIPMENT_FAILED]: 'errors:order.shipmentFailed',
  [ErrorCode.RETURN_NOT_ALLOWED]: 'errors:order.returnNotAllowed',
  [ErrorCode.ORDER_NOT_FOUND]: 'errors:order.orderNotFound',
  [ErrorCode.INVALID_ORDER_STATUS]: 'errors:order.invalidStatus',
  [ErrorCode.INACTIVE_CUSTOMER]: 'errors:order.inactiveCustomer',
  [ErrorCode.SHIPMENT_NOT_FOUND]: 'errors:order.shipmentNotFound',
  [ErrorCode.INVALID_SHIPMENT_QUANTITY]: 'errors:order.invalidShipmentQuantity',
  [ErrorCode.SHIPMENT_ALREADY_CONFIRMED]: 'errors:order.shipmentAlreadyConfirmed',
  [ErrorCode.RETURN_NOT_FOUND]: 'errors:order.returnNotFound',
  [ErrorCode.INVALID_RETURN_QUANTITY]: 'errors:order.invalidReturnQuantity',

  // Quotation / pricing errors
  [ErrorCode.QUOTATION_NOT_FOUND]: 'errors:quotation.notFound',
  [ErrorCode.QUOTATION_ALREADY_CONVERTED]: 'errors:quotation.alreadyConverted',
  [ErrorCode.QUOTATION_EXPIRED]: 'errors:quotation.expired',
  [ErrorCode.PRICE_LIST_NOT_FOUND]: 'errors:pricing.priceListNotFound',
  [ErrorCode.NO_ACTIVE_PRICE_LIST]: 'errors:pricing.noActivePriceList',
  [ErrorCode.PRICE_NOT_FOUND]: 'errors:pricing.priceNotFound',

  // Production specific errors
  [ErrorCode.BOM_NOT_FOUND]: 'errors:production.bomNotFound',
  [ErrorCode.WORK_ORDER_NOT_FOUND]: 'errors:production.workOrderNotFound',
  [ErrorCode.PRODUCTION_PLAN_CONFLICT]: 'errors:production.planConflict',
  [ErrorCode.MATERIAL_SHORTAGE]: 'errors:production.materialShortage',

  // Finance specific errors
  [ErrorCode.PAYMENT_FAILED]: 'errors:finance.paymentFailed',
  [ErrorCode.INVOICE_ALREADY_PAID]: 'errors:finance.invoiceAlreadyPaid',
  [ErrorCode.VOUCHER_NUMBER_CONFLICT]: 'errors:finance.voucherConflict',
  [ErrorCode.FISCAL_PERIOD_CLOSED]: 'errors:finance.fiscalPeriodClosed',

  // Database errors
  [ErrorCode.DATABASE_ERROR]: 'errors:system.database',
  [ErrorCode.CONNECTION_ERROR]: 'errors:system.connection',
  [ErrorCode.QUERY_ERROR]: 'errors:system.query',
  [ErrorCode.TRANSACTION_ERROR]: 'errors:system.transaction',
  [ErrorCode.CONSTRAINT_VIOLATION]: 'errors:system.constraint',
  [ErrorCode.UNIQUE_VIOLATION]: 'errors:system.unique',
  [ErrorCode.FOREIGN_KEY_VIOLATION]: 'errors:system.foreignKey',

  // External service errors
  [ErrorCode.EXTERNAL_SERVICE_ERROR]: 'errors:external.service',
  [ErrorCode.LLM_SERVICE_ERROR]: 'errors:external.llm',
  [ErrorCode.TIMEOUT_ERROR]: 'errors:external.timeout',
  [ErrorCode.RATE_LIMIT_EXCEEDED]: 'errors:external.rateLimit',

  // System errors
  [ErrorCode.INTERNAL_ERROR]: 'errors:system.internal',
  [ErrorCode.CONFIGURATION_ERROR]: 'errors:system.configuration',
  [ErrorCode.FEATURE_NOT_IMPLEMENTED]: 'errors:system.notImplemented',
  [ErrorCode.MAINTENANCE_MODE]: 'errors:system.maintenance',
  [ErrorCode.SERVICE_UNAVAILABLE]: 'errors:system.serviceUnavailable',
};

/**
 * Get the translation key for an error code
 */
export function getErrorTranslationKey(code: ErrorCode): string {
  return ERROR_CODE_KEY_MAP[code] || 'errors:system.unknown';
}

/**
 * Get the localized error message
 */
export function getLocalizedErrorMessage(
  code: ErrorCode,
  t: TFunction,
  fallback?: string
): string {
  const key = getErrorTranslationKey(code);
  const message = t(key);

  // If translation returns the key itself, use fallback or a default
  if (message === key) {
    return fallback || t('errors:system.unknown');
  }

  return message;
}

/**
 * Error message with interpolation support
 */
export interface ErrorContext {
  field?: string;
  value?: unknown;
  min?: number;
  max?: number;
  expected?: string;
  actual?: string;
  [key: string]: unknown;
}

/**
 * Get localized error message with context interpolation
 */
export function getLocalizedErrorMessageWithContext(
  code: ErrorCode,
  t: TFunction,
  context?: ErrorContext
): string {
  const key = getErrorTranslationKey(code);
  return t(key, context);
}

/**
 * Check if an error code is known
 */
export function isKnownErrorCode(code: string): code is ErrorCode {
  return code in ERROR_CODE_KEY_MAP;
}
